import React from 'react';
import { createAsyncInputComponent, useAsyncInput } from './AsyncInput';
import { useAccessToken } from '@app/auth';
import { fetchApi } from '@app/hooks/api';
import { RepositoryExistsResponse } from '../../../shared/types/microservice-generator';
import { getServiceNamePlaceholderWithType } from '../utils/formHelpers';

interface ServiceNameInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  validated?: 'error' | 'warning' | 'success' | 'default';
  isRequired?: boolean;
  error?: string;
  disabled?: boolean;
  // Dependencies for validation
  organization: string;
  techStack: string;
  functionalArea: string;
  domain: string;

}

// Create the ServiceNameInput component using the factory
export const ServiceNameInput = createAsyncInputComponent('ServiceNameInput', {
    getPlaceholder: ({ techStack, disabled }: { techStack?: string; disabled?: boolean }) => {
        if (disabled) {
            return 'Please select Organization, Tech Stack, Functional Area and Domain';
        }
        return getServiceNamePlaceholderWithType(techStack); // Always assumes 'new' service type
    },
    getAriaLabel: () => "Service Name Input",
    getValidated: ({ error, isValidating }: { error?: string; isValidating?: boolean }) => 
        error ? 'error' : (isValidating ? 'warning' : 'default')
});

// Hook for managing service name state and validation
export const useServiceNameInput = (
    initialValue: string = '',
    organization: string,
    techStack: string,
    functionalArea: string,
    domain: string
) => {
    const getToken = useAccessToken();

    // Sync validation function
    const syncValidator = (serviceName: string): string => {
        if (!serviceName.trim()) {
            return 'Service name is required';
        }

        if (serviceName.length < 3) {
            return 'Service name must be at least 3 characters long';
        }

        if (serviceName.length > 100) {
            return 'Service name must be less than 100 characters';
        }

        const validNamePattern = /^[a-zA-Z0-9_-]+$/;
        if (!validNamePattern.test(serviceName)) {
            return 'Service name can only contain letters, numbers, hyphens, and underscores';
        }

        if (/^[-_]|[-_]$/.test(serviceName)) {
            return 'Service name cannot start or end with hyphen or underscore';
        }

        return '';
    };

    // Async validation function
    const asyncValidator = async (serviceName: string): Promise<string> => {
        // Check if dependencies are met
        if (!organization || !techStack || !functionalArea || !domain) {
            return '';
        }

        try {
            const url = `/api/github/repository/exists?organization=${encodeURIComponent(organization)}&repository=${encodeURIComponent(serviceName)}&techStack=${encodeURIComponent(techStack)}`;
            const response = await fetchApi<RepositoryExistsResponse>(url, getToken);

            // For new services, repository should NOT exist (always 'new' now)
            if (response.exists) {
                return `Repository '${response.repository}' already exists in organization '${organization}'.`;
            }

            return '';
        } catch (error) {
            console.error('Error checking repository existence:', error);
            return 'Unable to verify repository availability. Please try again.';
        }
    };

    const asyncInput = useAsyncInput({
        required: true,
        syncValidator,
        asyncValidator,
        dependencies: [organization, techStack, functionalArea, domain],
        requiredMessage: 'Service name is required'
    });

    // Clear validation errors when dependencies are not met
    const isDependenciesMet = !!(organization && techStack && functionalArea && domain);
    React.useEffect(() => {
        if (!isDependenciesMet) {
            asyncInput.setError('');
        }
    }, [isDependenciesMet, asyncInput.setError]);

    return {
        serviceName: asyncInput.value,
        serviceNameError: asyncInput.error,
        isValidating: asyncInput.isValidating,
        isValid: asyncInput.isValid,
        isDependenciesMet,
        handleServiceNameChange: asyncInput.handleChange,
        handleServiceNameBlur: asyncInput.handleBlur,
        validateServiceName: asyncInput.validate
    };
};
